import config, { TDI_CONNECT_ROLE_PREFIX, withTdiConnectVariants } from '@/config';
import type { BaseUserRole } from '@/config';
import type { WithTdiConnectVariants } from '@/types';
import type { Organization } from '@/types/organization';

interface TargetUser {
  userId: string | undefined;
  createdBy: string;
  productId: string;
  userProducts: string[];
  osProjectType: string;
  userToBeUpdatedEmail?: string;
  userEmail: string;
  userToBeDeletedEmail?: string;
  userToBeDeletedRole: string;
  userToBeDeletedRoles?: string[];
  userToBeUpdatedRole: string;
  rowEmail?: string;
}

// `data: never` makes every dynamic-rule function (regardless of its real parameter type)
// assignable here, so `satisfies` below only checks that every base role has a definition,
// without widening `rules`'s inferred type (and losing each role's precise signatures).
interface RoleRuleSet {
  static: string[];
  dynamic: Record<string, (data: never) => unknown>;
}

export const APPROVED_CALC_EXCLUSION_ROLES = withTdiConnectVariants(['ADMIN', 'SUPPORT']);
const AO_AA_TL_DEV = withTdiConnectVariants([
  'ACCOUNT_OWNER',
  'ACCOUNT_ASSISTANT',
  'TECHNICAL_LEAD',
  'DEVELOPER',
]);
const AA_TL_DEV = withTdiConnectVariants(['ACCOUNT_ASSISTANT', 'TECHNICAL_LEAD', 'DEVELOPER']);
const TL_DEV = withTdiConnectVariants(['TECHNICAL_LEAD', 'DEVELOPER']);
const ADMIN_RULES = {
  static: [
    '/organizations:VIEW_ALL',
    '/organizations:VIEW_ALL_DETAILS',
    '/organization:POST',
    '/organization:PUT',
    '/reports',
    '/marketplace:VIEW',
    '/marketplace:PUT',
    '/forecast:VIEW',
    '/users:VIEW',
    '/users:POST',
    '/users:PUT:ORG',
    '/projects:VIEW',
    '/projects:POST',
    '/projects:PATCH',
    '/projects:DELETE',
    '/osprojects:DELETE',
    '/osprojects:PATCH',
    '/products:VIEW',
    '/products:POST',
    '/products:PUT',
    '/products:PUT:TAGS',
    '/products:CONTACTS_VIEW',
    '/products:CONTACTS_PATCH',
    '/products:CONTACTS_DELETE',
    '/products:DELETE',
    '/products:assign',
    '/reports/caas-clusters:PATCH',
    '/onboarding/admin-review',
  ],
  dynamic: {
    '/users:PUT': (data: TargetUser) => data.userToBeUpdatedEmail !== data.userEmail,
    '/users:DELETE': (data: TargetUser) => data.userToBeDeletedEmail !== data.userEmail,
    '/users:PUT_ROLE_CHECK': (enterpriseName: string) =>
      enterpriseName === 'admin-org' ? config.elevatedPrivilegeRoles : AO_AA_TL_DEV,
  },
};

const rules = {
  ADMIN: ADMIN_RULES,
  SUPPORT: {
    static: [
      '/organizations:VIEW_ALL',
      '/reports',
      '/users:VIEW',
      '/marketplace:VIEW',
      '/projects:VIEW',
      '/products:VIEW',
      '/products:CONTACTS_VIEW',
    ],
    dynamic: {},
  },
  CAAS_ADMIN: {
    static: [
      '/organizations:VIEW_ALL',
      '/reports',
      '/users:VIEW',
      '/marketplace:VIEW',
      '/projects:VIEW',
      '/products:VIEW',
      '/products:CONTACTS_VIEW',
      '/reports/caas-clusters:PATCH',
    ],
    dynamic: {},
  },
  ACCOUNT_OWNER: {
    static: [
      '/organizations:VIEW_OWN_DETAILS',
      '/users:VIEW',
      '/users:POST',
      '/projects:VIEW',
      '/projects:PATCH',
      '/products',
      '/products:VIEW',
      '/settings/cloud-reset-password',
      '/products:assign',
    ],
    dynamic: {
      '/users:DELETE': (data: TargetUser) =>
        data.userToBeDeletedEmail !== data.userEmail &&
        AO_AA_TL_DEV.includes(data.userToBeDeletedRole),
      '/users:PUT': (data: TargetUser) => AA_TL_DEV.includes(data.userToBeUpdatedRole),
      '/users:PUT_ROLE_CHECK': () => AO_AA_TL_DEV,
      '/products/compute/order': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Compute') !== undefined,
      '/products/compute/free-trial': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Evaluation') !== undefined,
      '/osprojects:DELETE': (data: TargetUser) =>
        data.osProjectType !== 'computeTrial' && data.userProducts.includes(data.productId),
    },
  },
  ACCOUNT_ASSISTANT: {
    static: [
      '/organizations:VIEW_OWN_DETAILS',
      '/users:VIEW',
      '/users:POST',
      '/projects:VIEW',
      '/projects:PATCH',
      '/products',
      '/settings/cloud-reset-password',
      '/products:assign',
    ],
    dynamic: {
      '/users:DELETE': (data: TargetUser) =>
        data.userToBeDeletedEmail !== data.userEmail &&
        AA_TL_DEV.includes(data.userToBeDeletedRole),
      '/users:PUT': (data: TargetUser) => TL_DEV.includes(data.userToBeUpdatedRole),
      '/users:PUT_ROLE_CHECK': () => AA_TL_DEV,
      '/products/compute/order': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Compute') !== undefined,
      '/products/compute/free-trial': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Evaluation') !== undefined,
      '/osprojects:DELETE': (data: TargetUser) =>
        data.osProjectType !== 'computeTrial' && data.userProducts.includes(data.productId),
    },
  },
  TECHNICAL_LEAD: {
    static: [
      '/organizations:VIEW_OWN_DETAILS',
      '/products',
      '/projects:VIEW',
      '/products:PUT',
      '/settings/cloud-reset-password',
    ],
    dynamic: {
      '/users:DELETE': (data: TargetUser) =>
        data.userToBeDeletedEmail !== data.userEmail && TL_DEV.includes(data.userToBeDeletedRole),
      '/users:PUT_ROLE_CHECK': () => TL_DEV,
      '/products/compute/order': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Compute') !== undefined,
      '/products/compute/free-trial': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Evaluation') !== undefined,
      '/osprojects:DELETE': (data: TargetUser) =>
        data.osProjectType !== 'computeTrial' && data.userProducts.includes(data.productId),
    },
  },
  DEVELOPER: {
    static: [
      '/organizations:VIEW_OWN_DETAILS',
      '/projects:VIEW',
      '/products',
      '/settings/cloud-reset-password',
    ],
    dynamic: {
      '/products/compute/order': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Compute') !== undefined,
      '/products/compute/free-trial': (organization: Organization) =>
        organization.marketplaceItems?.find(item => item.name === 'Evaluation') !== undefined,
      '/osprojects:DELETE': (data: TargetUser) =>
        data.osProjectType !== 'computeTrial' && data.userProducts.includes(data.productId),
    },
  },
} satisfies Record<BaseUserRole, RoleRuleSet>;

const extendedRules = {
  ...rules,
  ...Object.fromEntries(
    config.baseUserRoles.map(role => [`${TDI_CONNECT_ROLE_PREFIX}${role}`, rules[role]])
  ),
} as WithTdiConnectVariants<typeof rules>;

export default extendedRules;
